import type { SupportedProductsResponse } from '../../../../../api/accounts.api';
import type { SubmissionDetailsDto } from '../../../../../api/submissions/submissions.api.types';
import {
  getCountryApiParam,
  toCountryApiParam,
} from '../../../../../i18n/country';
import { ProductSuite, type ProductType } from '../../../../../types';
import ProductStatics from '../../../../_statics/product.statics.json';
import { resolveEligibleProductSelection } from '../../../../../components/inputs/selects/eligibleProductSelection';
import { getPlatformRegion } from '../../../../../utils/app.utils';
import { treeProductOf } from '../../../../../utility/filter.expansions';

export type ProductLineInfo = { key: string; name: string };

/**
 * The country whose products a submission can be offered: its physical address's country, or the
 * platform's own when the address has none (or one the platform does not sell in).
 */
export function getSubmissionProductCountry(
  addressCountry: string | null | undefined,
  platformCountry: string
): string {
  return toCountryApiParam(addressCountry) ?? platformCountry;
}

/**
 * The country whose products a submission is offered, read from the submission: the one its quote flows read the
 * supported products for.
 */
export function submissionProductCountry(
  submission: Pick<SubmissionDetailsDto, 'firmographics'> | undefined
): string {
  return getSubmissionProductCountry(
    submission?.firmographics?.physicalAddress?.country,
    getCountryApiParam(getPlatformRegion())
  );
}

type SupportedProductLines = NonNullable<
  SupportedProductsResponse['supportedProductLines']
>;

function findRootProductLine(
  item: SupportedProductLines[number],
  allItems: SupportedProductLines
): SupportedProductLines[number] {
  let current = item;
  while (current.productLine.parentId) {
    const parent = allItems.find(
      (sp) => sp.productLine.id === current.productLine.parentId
    );
    if (!parent) break;
    current = parent;
  }
  return current;
}

const rootIdOf = (key: string, all: SupportedProductLines) => {
  const line = all.find((sp) => sp.productLine.key === key);
  return line && findRootProductLine(line, all).productLine.id;
};

/**
 * Whether a submission is on several lines of business that a product has to be told apart on: its lines fall under
 * more than one root, and at least one of those roots has child lines, as Management Liability does for its coverages.
 * Cyber and Tech E&O on their own are two roots with no child lines, which is not mixed. False until the supported
 * products are known.
 *
 * Agrees with fir-account's `ProductProductLineService.spansSeveralLinesOfBusiness` for today's roots.
 */
export function isMixedSubmission(
  lineKeys: readonly string[],
  supportedProducts: SupportedProductsResponse | undefined
): boolean {
  const all = supportedProducts?.supportedProductLines ?? [];
  const roots = new Set(
    lineKeys.map((key) => rootIdOf(key, all)).filter(Boolean) as string[]
  );
  return (
    roots.size > 1 &&
    Array.from(roots).some((root) =>
      all.some((sp) => sp.productLine.parentId === root)
    )
  );
}

/**
 * The product lines a product is rated on, given the lines the submission is on.
 *
 * A submission that is not mixed (`isMixedSubmission`) is rated on its lines as they stand, whatever the product.
 *
 * On a mixed submission a product is rated on the lines under its own root. For a root with child lines that is the
 * child lines the submission is on, so ZSP is rated on the coverages chosen (Crime and EPL), never on the root line
 * itself, and on all its child lines where it is on none. Prime 100 Pro is rated on Cyber. A product whose root has no
 * child lines and none of whose lines the submission is on is rated on the submission's lines.
 *
 * The lines stand as they are while the supported products are unknown, and for a product no line lists. A product is
 * listed as the platform lists its offering (`treeProductOf`: P250X as P250).
 */
export function linesForRating(
  productType: string,
  lineKeys: readonly string[],
  supportedProducts: SupportedProductsResponse | undefined
): string[] {
  const all = supportedProducts?.supportedProductLines ?? [];
  const lines = [...lineKeys];
  const listed = treeProductOf(productType);
  const productLine = all.find(
    (sp) => sp.products.includes(productType) || sp.products.includes(listed)
  );
  if (!productLine || !isMixedSubmission(lineKeys, supportedProducts)) {
    return lines;
  }

  const productRoot = findRootProductLine(productLine, all).productLine.id;
  const childKeys = all
    .filter((sp) => sp.productLine.parentId === productRoot)
    .map((sp) => sp.productLine.key);
  if (childKeys.length > 0) {
    const chosen = lines.filter((key) => childKeys.includes(key));
    return chosen.length > 0 ? chosen : childKeys;
  }
  const own = lines.filter((key) => rootIdOf(key, all) === productRoot);
  return own.length > 0 ? own : lines;
}

/**
 * The name of the first line a product is rated on (`linesForRating`): what a quote of one part, which has no parts of
 * its own, is saved under. The submission names its own lines; a line it doesn't have, as ZSP's on a Cyber submission,
 * is named by the supported products.
 */
export function firstRatedLineName(
  productType: string,
  productLines: ReadonlyArray<{ key: string; name: string }> | undefined,
  supportedProducts: SupportedProductsResponse | undefined
): string | undefined {
  const [key] = linesForRating(
    productType,
    (productLines ?? []).map((line) => line.key),
    supportedProducts
  );
  return (
    productLines?.find((line) => line.key === key)?.name ??
    supportedProducts?.supportedProductLines.find(
      (sp) => sp.productLine.key === key
    )?.productLine.name
  );
}

/** Every product line a coverage option can cover: each suite's child lines, or the suite itself when it has none. */
export function getAllProductLines(
  supportedProducts: SupportedProductsResponse | undefined
): ProductLineInfo[] {
  const all = supportedProducts?.supportedProductLines;
  if (!all) return [];

  return all
    .filter((sp) => sp.productLine.parentId === null)
    .flatMap((root) => {
      const children = all.filter(
        (sp) => sp.productLine.parentId === root.productLine.id
      );
      return children.length > 0 ? children : [root];
    })
    .map((sp) => ({ key: sp.productLine.key, name: sp.productLine.name }));
}

/**
 * The product lines a coverage option can be put on: each line one of whose products the eligible-products API
 * returned for the submission (`getProductOptionsForLine`), so a line is never offered with an empty Product list.
 * The lines an option being edited already covers stay, whatever their eligibility.
 */
export function getOfferedProductLines(
  supportedProducts: SupportedProductsResponse | undefined,
  eligibleProducts: ReadonlyArray<{ productType: string }> | undefined,
  ownLineNames: readonly string[] = []
): ProductLineInfo[] {
  return getAllProductLines(supportedProducts).filter(
    (line) =>
      ownLineNames.includes(line.name) ||
      getProductOptionsForLine(line.key, supportedProducts, eligibleProducts)
        .length > 0
  );
}

/** The lines that can share an option with the given one: its suite's child lines, the given line first. */
export function getSuiteLines(
  lineKey: string,
  supportedProducts: SupportedProductsResponse | undefined
): ProductLineInfo[] {
  const all = supportedProducts?.supportedProductLines ?? [];
  const line = all.find((sp) => sp.productLine.key === lineKey);
  if (!line) return [];

  const parentId = line.productLine.parentId;
  const siblings =
    parentId === null
      ? []
      : all.filter(
          (sp) =>
            sp.productLine.parentId === parentId &&
            sp.productLine.key !== lineKey
        );
  return [line, ...siblings].map((sp) => ({
    key: sp.productLine.key,
    name: sp.productLine.name,
  }));
}

/**
 * The products to offer for a chosen product line: those the line lists, and for a child line its suite's (ZSP for
 * Crime), kept to the ones the eligible-products API returned for the submission. A product it left out is not
 * offered. The product already on an option being edited stays selectable.
 */
export function getProductOptionsForLine(
  lineKey: string,
  supportedProducts: SupportedProductsResponse | undefined,
  eligibleProducts: ReadonlyArray<{ productType: string }> | undefined,
  currentProduct?: string
): { label: string; value: string }[] {
  const all = supportedProducts?.supportedProductLines ?? [];
  const line = all.find((sp) => sp.productLine.key === lineKey);
  if (!line) return [];
  const parent = line.productLine.parentId
    ? all.find((sp) => sp.productLine.id === line.productLine.parentId)
    : undefined;
  // A child line's parts (ZSP_CRIME) are quoted under the suite's product, so the suite's product is the one offered.
  const lineProducts = parent?.products.length
    ? parent.products
    : line.products;
  const eligible = new Set(
    (eligibleProducts ?? []).map((product) => product.productType)
  );
  const types = lineProducts.filter((type) => eligible.has(type));
  if (currentProduct && !types.includes(currentProduct)) {
    types.push(currentProduct);
  }
  return resolveEligibleProductSelection(
    types.map((productType) => ({ productType }))
  ).options;
}

/** The suite is the root of a submission's product lines; the server no longer sends it. */
export function getProductSuiteFromProductLines(
  productLines: ReadonlyArray<{ key: string }> | undefined,
  supportedProducts: SupportedProductsResponse | undefined
): string | undefined {
  const all = supportedProducts?.supportedProductLines;
  if (!all || !productLines) return undefined;

  const item = all.find((sp) =>
    productLines.some((line) => line.key === sp.productLine.key)
  );
  if (!item) return undefined;

  return findRootProductLine(item, all).productLine.name;
}

/**
 * Whether the submission is quoted on Cyber or Tech E&O rather than Management Liability. False until the suite is known, so an ML submission never shows Cyber-only panels while they load, on failure, or when no line matches.
 */
export function isCyberOrTechEOSubmission(
  productLines: ReadonlyArray<{ key: string }> | undefined,
  supportedProducts: SupportedProductsResponse | undefined
): boolean {
  const suite = getProductSuiteFromProductLines(
    productLines,
    supportedProducts
  );
  return suite !== undefined && suite !== ProductSuite.ML;
}

export function getProductLineKeyFromName(
  name: string,
  supportedProducts: SupportedProductsResponse | undefined
): string | undefined {
  if (!supportedProducts?.supportedProductLines) return undefined;

  const match = supportedProducts.supportedProductLines.find(
    (sp) => sp.productLine.name === name
  );
  return match?.productLine.key;
}

export const SUBMISSION_VIEWS = {
  quote: 'quote',
  summary: 'summary',
} as const;

/**
 * Names of the product lines a submission is quoted on: its own lines first, then any line a coverage option
 * covers that they do not already include. Coverage options can add lines (a Cyber option on a Tech E&O submission),
 * so the submission's own lines alone understate them.
 */
export function getSubmissionProductLineNames(
  productLines: ReadonlyArray<{ name: string }> | undefined,
  coverageOptions: ReadonlyArray<{
    coverages: ReadonlyArray<{ part: { value?: unknown } }>;
  }> = []
): string[] {
  const names = (productLines ?? []).map((line) => line.name);
  coverageOptions.forEach((option) =>
    option.coverages.forEach((coverage) => {
      const name = coverage.part.value;
      if (typeof name === 'string' && name && !names.includes(name)) {
        names.push(name);
      }
    })
  );
  return names;
}

const PRODUCT_LINE_LABELS: Record<string, string> = {
  'Directors and Officers': 'D&O',
  'Employment Practices Liability': 'EPL',
  'Fiduciary Liability': 'Fiduciary',
  Crime: 'Crime',
  Cyber: 'Cyber',
  CYBER: 'Cyber',
  TECH_ENO: 'Tech E&O',
};

type CoverageOptionLike = {
  readonly product?: string | null;
  readonly coverages: ReadonlyArray<{ part: { value?: unknown } }>;
};

/** Product Line(s) as the Summary and Coverages panels both show it. */
export function formatSubmissionProductLines(
  productLines: ReadonlyArray<{ name: string }> | undefined,
  coverageOptions: ReadonlyArray<CoverageOptionLike> = []
): string {
  const labels = getSubmissionProductLineNames(
    productLines,
    coverageOptions
  ).map((name) => PRODUCT_LINE_LABELS[name] || name);
  return labels.filter((l, i) => labels.indexOf(l) === i).join(' | ') || '-';
}

/** Product as the Summary and Coverages panels both show it: each coverage option's product, once. */
export function formatCoverageProducts(
  coverageOptions: ReadonlyArray<CoverageOptionLike> = []
): string {
  const names = coverageOptions
    .map(
      (option) =>
        ProductStatics.ProductTypes[option.product as ProductType]?.full
    )
    .filter((name): name is string => !!name && name.trim() !== '');
  return names.filter((n, i) => names.indexOf(n) === i).join(' | ') || '-';
}

/** The products a submission's coverage options name, once each and sorted: what its merged questions are for. */
export function coverageOptionProductKeys(
  coverageOptions: ReadonlyArray<{ product?: string | null }> | undefined
): string[] {
  const keys = (coverageOptions ?? [])
    .map((option) => option.product)
    .filter((product): product is string => !!product);
  return Array.from(new Set(keys)).sort();
}
